import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/joy/Box';
import Drawer from '@mui/joy/Drawer';
import List from '@mui/joy/List';
import ListItemButton from '@mui/joy/ListItemButton';
import ListItemDecorator from '@mui/joy/ListItemDecorator';
import Typography from '@mui/joy/Typography';
import ModalClose from '@mui/joy/ModalClose';
import Menu from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate, useLocation } from 'react-router-dom';
import { CssBaseline, ThemeProvider } from '@mui/joy';
import { theme } from '../../main/theme/constants';
import { useAuth } from '../../main/context/AuthContext';
import { primaryColor } from '../../main/theme/constants';
import { useTranslation } from 'react-i18next';
import { isRtlLocale } from '../../../i18n';

export default function CustomerDrawer({ open, onClose, restrictNavToHome = false }) {
  const { t, i18n } = useTranslation('ui');
  const drawerAnchor = isRtlLocale(i18n.language) ? 'right' : 'left';
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  // Function to check if a route is active
  const isActiveRoute = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLogout = () => {
    logout();
    navigate('/customer-login');
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Drawer
        anchor={drawerAnchor}
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-content': {
            width: 320,
            maxWidth: '100vw',
            background: '#ffffff',
            color: '#333',
            ...(drawerAnchor === 'left'
              ? { borderRight: '1px solid #f0f0f0' }
              : { borderLeft: '1px solid #f0f0f0' }),
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          },
        }}
      >
          {/* Header Section */}
          <Box
            sx={{
              background: '#f8f9fa',
              padding: 3,
              color: '#333',
              borderBottom: '1px solid #e9ecef',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ 
                  backgroundColor: '#e9ecef', 
                  borderRadius: '8px', 
                  padding: 1,
                  marginRight: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Menu sx={{ fontSize: 20, color: '#666' }} />
                </Box>
                <Typography fontSize="xl" fontWeight="bold" sx={{ color: '#333' }}>
                  {t('customer.menuTitle')}
                </Typography>
              </Box>
              <ModalClose
                onClick={onClose}
                sx={{ 
                  color: '#666',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                  }
                }}
              />
            </Box>
          </Box>

          {/* Navigation Items */}
          <List
            size="lg"
            component="nav"
            sx={{
              padding: 2,
              '& .MuiListItemButton-root': {
                borderRadius: '8px',
                margin: '2px 0',
                padding: '10px 12px',
                transition: 'all 0.2s ease',
                border: '1px solid transparent',
                '&:hover': {
                  backgroundColor: '#f8f9fa',
                  transform: 'translateX(2px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  border: '1px solid #e9ecef',
                },
              },
              '& .MuiListItemDecorator-root': {
                minWidth: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              },
            }}
          >
            {/* Home */}
            <ListItemButton 
              onClick={() => { onClose(); navigate("/customer-home"); }}
              sx={{
                backgroundColor: isActiveRoute('/customer-home') ? '#f5f5f5' : 'transparent',
                border: isActiveRoute('/customer-home') ? '1px solid #d0d0d0' : '1px solid transparent',
                '&:hover': {
                  backgroundColor: isActiveRoute('/customer-home') ? '#f5f5f5' : '#f8f9fa',
                  border: isActiveRoute('/customer-home') ? '1px solid #d0d0d0' : '1px solid #e9ecef',
                }
              }}
            >
              <ListItemDecorator>
                <Box sx={{ 
                  backgroundColor: isActiveRoute('/customer-home') ? '#e0e0e0' : '#f8f9fa', 
                  borderRadius: '6px', 
                  padding: 0.8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <HomeIcon sx={{ fontSize: 18, color: isActiveRoute('/customer-home') ? primaryColor : '#6c757d' }} />
                </Box>
              </ListItemDecorator>
              <Typography sx={{ 
                fontWeight: '500', 
                color: isActiveRoute('/customer-home') ? primaryColor : '#495057' 
              }}>
                {t('customer.home')}
              </Typography>
            </ListItemButton>

            {/* My Shipments */}
            {!restrictNavToHome && (
            <>
            <ListItemButton 
              onClick={() => { onClose(); navigate("/customer-shipments"); }}
              sx={{
                backgroundColor: isActiveRoute('/customer-shipments') ? '#f5f5f5' : 'transparent',
                border: isActiveRoute('/customer-shipments') ? '1px solid #d0d0d0' : '1px solid transparent',
                '&:hover': {
                  backgroundColor: isActiveRoute('/customer-shipments') ? '#f5f5f5' : '#f8f9fa',
                  border: isActiveRoute('/customer-shipments') ? '1px solid #d0d0d0' : '1px solid #e9ecef',
                }
              }}
            >
              <ListItemDecorator>
                <Box sx={{ 
                  backgroundColor: isActiveRoute('/customer-shipments') ? '#e0e0e0' : '#f8f9fa', 
                  borderRadius: '6px', 
                  padding: 0.8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <LocalShippingIcon sx={{ fontSize: 18, color: isActiveRoute('/customer-shipments') ? primaryColor : '#6c757d' }} />
                </Box>
              </ListItemDecorator>
              <Typography sx={{ 
                fontWeight: '500', 
                color: isActiveRoute('/customer-shipments') ? primaryColor : '#495057' 
              }}>
                {t('customer.myShipments')}
              </Typography>
            </ListItemButton>

            {/* My Invoices */}
            <ListItemButton 
              onClick={() => { onClose(); navigate("/customer-invoices"); }}
              sx={{
                backgroundColor: isActiveRoute('/customer-invoices') ? '#f5f5f5' : 'transparent',
                border: isActiveRoute('/customer-invoices') ? '1px solid #d0d0d0' : '1px solid transparent',
                '&:hover': {
                  backgroundColor: isActiveRoute('/customer-invoices') ? '#f5f5f5' : '#f8f9fa',
                  border: isActiveRoute('/customer-invoices') ? '1px solid #d0d0d0' : '1px solid #e9ecef',
                }
              }}
            >
              <ListItemDecorator>
                <Box sx={{ 
                  backgroundColor: isActiveRoute('/customer-invoices') ? '#e0e0e0' : '#f8f9fa', 
                  borderRadius: '6px', 
                  padding: 0.8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <ReceiptIcon sx={{ fontSize: 18, color: isActiveRoute('/customer-invoices') ? primaryColor : '#6c757d' }} />
                </Box>
              </ListItemDecorator>
              <Typography sx={{ 
                fontWeight: '500', 
                color: isActiveRoute('/customer-invoices') ? primaryColor : '#495057' 
              }}>
                {t('customer.myInvoices')}
              </Typography>
            </ListItemButton>

            {/* My Payments */}
            <ListItemButton 
              onClick={() => { onClose(); navigate("/customer-payments"); }}
              sx={{
                backgroundColor: isActiveRoute('/customer-payments') ? '#f5f5f5' : 'transparent',
                border: isActiveRoute('/customer-payments') ? '1px solid #d0d0d0' : '1px solid transparent',
                '&:hover': {
                  backgroundColor: isActiveRoute('/customer-payments') ? '#f5f5f5' : '#f8f9fa',
                  border: isActiveRoute('/customer-payments') ? '1px solid #d0d0d0' : '1px solid #e9ecef',
                }
              }}
            >
              <ListItemDecorator>
                <Box sx={{ 
                  backgroundColor: isActiveRoute('/customer-payments') ? '#e0e0e0' : '#f8f9fa', 
                  borderRadius: '6px', 
                  padding: 0.8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <PaymentIcon sx={{ fontSize: 18, color: isActiveRoute('/customer-payments') ? primaryColor : '#6c757d' }} />
                </Box>
              </ListItemDecorator>
              <Typography sx={{ 
                fontWeight: '500', 
                color: isActiveRoute('/customer-payments') ? primaryColor : '#495057' 
              }}>
                {t('customer.myPayments')}
              </Typography>
            </ListItemButton>
            </>
            )}

            {/* Divider */}
            <Box sx={{ my: 2, borderTop: '1px solid #e9ecef' }} />

            {/* Logout */}
            <ListItemButton 
              onClick={handleLogout}
              sx={{
                marginTop: 2,
                backgroundColor: '#f8f9fa',
                border: '1px solid #e9ecef',
                '&:hover': {
                  backgroundColor: '#e9ecef',
                  border: '1px solid #dee2e6',
                }
              }}
            >
              <ListItemDecorator>
                <Box sx={{ 
                  backgroundColor: '#e9ecef', 
                  borderRadius: '6px', 
                  padding: 0.8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <LogoutIcon sx={{ fontSize: 18, color: '#dc2626' }} />
                </Box>
              </ListItemDecorator>
              <Typography sx={{ fontWeight: '500', color: '#dc2626' }}>
                {t('actions.logout')}
              </Typography>
            </ListItemButton>
          </List>
      </Drawer>
    </ThemeProvider>
  );
}

CustomerDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  restrictNavToHome: PropTypes.bool,
};

